import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography, makeStyles } from '@material-ui/core';
import Highcharts from 'highcharts';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import HighchartsReact from 'highcharts-react-official';
import { AtCircularProgress } from '../../../components/AtCircularProgress';

const useStyles = makeStyles((theme) => ({
    metricCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '20px',
        borderRadius: '16px',
        background: '#F9FAFB',
        height: '100%',
        minHeight: '110px',
        boxSizing: 'border-box',
        transition: 'all 0.2s ease',
        '&:hover': {
          background: '#F3F4F6',
          transform: 'translateY(-2px)'
        }
    },
    title: {
        fontWeight: 700,
        fontSize: '1.25rem',
        color: '#111827',
    },
    gridItem: {
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.up('sm')]: {
            minHeight: '50%',
        }
    }
}));

const Sparkline = React.memo(({ color, data }) => {
    const chartData = React.useMemo(() => 
      Array.isArray(data) && data.length > 0 ? data : [0, 0, 0, 0, 0, 0, 0]
    , [data]);

    const chartOptions = React.useMemo(() => ({
      chart: {
        type: 'areaspline',
        margin: [2, 0, 2, 0],
        height: 35,
        width: 120,
        backgroundColor: 'transparent',
        skipClone: true
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: { visible: false },
      yAxis: { visible: false },
      legend: { enabled: false },
      tooltip: { enabled: false },
      plotOptions: {
        areaspline: {
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, Highcharts.color(color).setOpacity(0.2).get('rgba')],
              [1, Highcharts.color(color).setOpacity(0).get('rgba')]
            ]
          },
          marker: { enabled: false },
          lineWidth: 2,
          lineColor: color,
          states: { hover: { enabled: false } }
        }
      },
      series: [{
        data: chartData,
        enableMouseTracking: false
      }]
    }), [color, chartData]);
  
    return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
});

const KpiCard = React.memo(({ kpi, classes }) => {
  const { t } = useTranslation();

  if (kpi.loading) {
    return (
      <Grid item xs={12} sm={6} className={classes.gridItem}>
        <Box display="flex" justifyContent="center" alignItems="center" style={{ height: '100%' }}>
          <AtCircularProgress />
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} sm={6} className={classes.gridItem}>
      <Box className={classes.metricCard}>
        <Typography
          variant="caption"
          style={{ color: '#9CA3AF' }}
        >
          {t(kpi.labelKey)}
        </Typography>

        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          gridGap="8px"
        >
          <Typography
            variant="h5"
            style={{
              fontWeight: 700,
              color: '#111827',
            }}
          >
            {kpi.value}
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            style={{
              color:
                kpi.trendType === 'up'
                  ? '#10B981'
                  : '#EF4444',
            }}
          >
            {kpi.trendType === 'up' ? (
              <TrendingUpIcon fontSize="small" />
            ) : (
              <TrendingDownIcon fontSize="small" />
            )}
          </Box>

          <Box flex={1} minWidth={120}>
            <Sparkline
              color={kpi.color}
              data={kpi.sparklineData}
            />
          </Box>
        </Box>

        {kpi.details && kpi.details.length > 0 && (
          <Box display="flex" flexWrap="wrap" gridGap="4px" mt="auto">
            {kpi.details.map((detail, idx) => (
              detail.label !== 'Uptime Base' && <Box key={idx} style={{ 
                  background: '#F3F4F6', 
                  padding: '2px 4px', 
                  borderRadius: '4px', 
                  border: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
              }}>
                <Typography variant="caption" style={{ color: '#6B7280', fontSize: '0.6rem', fontWeight: 600, lineHeight: 1 }}>
                  {detail.label}:
                </Typography>
                <Typography variant="caption" style={{ color: '#374151', fontSize: '0.6rem', fontWeight: 700, lineHeight: 1 }}>
                  {detail.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Grid>
  );
});

export const KpisObjetivos = ({ kpis }) => {
    const classes = useStyles();

    return (
        <>
          {kpis.map((kpi) => (
            <KpiCard key={kpi.id} kpi={kpi} classes={classes} />
          ))}
        </>
    );
};